import {motion} from 'framer-motion';
import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  DataStatus,
  Form,
  FormButton,
  FormField,
} from 'platform/components';
import {Box, Grid, GridItem, Heading, Icon, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';
import {DeepPartial, UseFormReturn} from 'react-hook-form';

import {always, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {CustomerMessageIdEnum, useLazyGetLastKnownSmtpCredentialsQuery} from '@dms/api/core';
import {useGetCustomerV2Query} from '@dms/api/customer';
import {EntityResourceIds} from '@dms/api/shared';
import {useGetServiceCheckInQuery, useGetServiceHandOverQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {
  EmailCustomerSelectField as CustomerSelectField,
  type EmailFormValues as FormValues,
  EmailMessageUpload as MessageUpload,
  getCustomerEmails,
  handleApiError,
  SendCustomerEmailFormSchema,
  type UploadedCustomerMessageFile,
  useSmtpOptions,
} from '@dms/shared';

import {ApiError, Nullish} from 'shared/web';

import {EmailPreview} from './components/EmailPreview';
import {MessageFormField} from './components/MessageFormField';
import {useGetDefaultCustomerOptions} from './hooks/useGetDefaultCustomerOptions';
import {useGetMessageTypeOptions} from './hooks/useGetMessageTypeOptions';
import {useSubmitAftersalesEmail} from './hooks/useSubmitAftersalesEmail';

interface SendAfterSalesEmailFormProps {
  serviceCaseId: string;
  serviceOrderId?: string;
  vehicleId?: string | Nullish;
  defaultValues?: {
    attachments?: UploadedCustomerMessageFile[];
    customerEmailAddress?: string | Nullish;
    subject?: string | Nullish;
    body?: string | Nullish;
    defaultCategory?: FormValues['customerMessageId'][number];
  };
}

export function SendAfterSalesEmailForm(props: SendAfterSalesEmailFormProps) {
  const [getLastKnownSmtpCredentials] = useLazyGetLastKnownSmtpCredentialsQuery();

  const customerMessageId: CustomerMessageIdEnum =
    props.defaultValues?.defaultCategory ?? 'customer-message';
  const {defaultSmtpCredentialId, isLoading: isLoadingSmtpOptions} =
    useSmtpOptions(customerMessageId);

  const {
    messageTypeOptions,
    isLoading: isLoadingMessageTypeOptions,
    isError: isErrorMessageTypeOptions,
  } = useGetMessageTypeOptions(props.serviceCaseId);

  const {
    defaultCustomerOptions,
    customerId,
    isLoading: isLoadingDefaultCustomerOptions,
  } = useGetDefaultCustomerOptions({
    serviceCaseId: props.serviceCaseId,
    serviceOrderId: props.serviceOrderId,
  });

  const {data: checkIn, isLoading: isCheckInLoading} = useGetServiceCheckInQuery({
    serviceCaseId: props.serviceCaseId,
  });

  const {data: handOver, isLoading: isLoadingHandOver} = useGetServiceHandOverQuery({
    serviceCaseId: props.serviceCaseId,
  });

  const {data: customer, isLoading: isLoadingCustomer} = useGetCustomerV2Query(
    {
      customerId: customerId ?? '',
    },
    {skip: isNilOrEmpty(customerId)}
  );

  const [uploadedFiles, setUploadedFiles] = useState<UploadedCustomerMessageFile[]>(
    props.defaultValues?.attachments ?? []
  );

  const [handleSubmit] = useSubmitAftersalesEmail({
    serviceCaseId: props.serviceCaseId,
    uploadedFiles,
  });

  const defaultValues: DeepPartial<FormValues> = {
    customerId: customerId ?? undefined,
    customerEmailAddress:
      props.defaultValues?.customerEmailAddress ?? getCustomerEmails(customer)[0]?.value,
    subject: props.defaultValues?.subject ?? undefined,
    body: props.defaultValues?.body ?? undefined,
    customerMessageId: [props.defaultValues?.defaultCategory ?? 'customer-message'],
    smtpCredentialsId: defaultSmtpCredentialId,
  };

  const isLoading =
    isLoadingCustomer ||
    isLoadingMessageTypeOptions ||
    isLoadingSmtpOptions ||
    isLoadingDefaultCustomerOptions ||
    isLoadingHandOver ||
    isCheckInLoading;
  const isError = isErrorMessageTypeOptions;

  const UploadFromSystemProps = props.serviceOrderId
    ? {
        recordId: props.serviceOrderId,
        resourceId: EntityResourceIds.serviceOrder,
      }
    : {
        recordId: props.serviceCaseId,
        resourceId: EntityResourceIds.serviceCase,
      };

  const handleChipsChange = (formApi: UseFormReturn<FormValues>, value: string[] | Nullish) => {
    if (isNotNil(value) && !value.includes('customer-message')) {
      formApi.setValue('subject', undefined);
      formApi.setValue('body', undefined);
    }

    const messageId = value?.[0];

    if (isNotNil(messageId)) {
      getLastKnownSmtpCredentials({customerMessageId: messageId as CustomerMessageIdEnum})
        .unwrap()
        .then(({smtpCredentialsId}) => {
          if (isNotNil(smtpCredentialsId)) {
            formApi.setValue('smtpCredentialsId', smtpCredentialsId);
          }
        })
        .catch((err: ApiError) => handleApiError(err, {silent: true}));
    }
  };

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight={160}>
      <Box position="relative">
        <motion.div>
          <Form<FormValues>
            defaultValues={defaultValues}
            experimentalZodSchema={SendCustomerEmailFormSchema}
            onSubmit={handleSubmit}
          >
            {(control, formApi) => {
              const messageId = formApi.watch('customerMessageId');

              const inspectionId = match(messageId)
                .with(['service-customer_check_in'], () => checkIn?.inspectionId?.[0])
                .with(['service-customer_handover'], () => handOver?.inspectionId?.[0])
                .otherwise(() => undefined);

              const description = match(messageId)
                .with(
                  ['service-customer_check_in'],
                  always(i18n.t('entity.customer.labels.checkin_title_description'))
                )
                .with(
                  ['service-customer_handover'],
                  always(i18n.t('entity.customer.labels.handover_title_description'))
                )
                .otherwise(
                  always(i18n.t('entity.customer.labels.customer_message_title_description'))
                );

              return (
                <Grid columns={2}>
                  <VStack spacing={2} minHeight={167}>
                    <Box paddingHorizontal={10}>
                      <VStack spacing={4} align="center" justify="center">
                        <Icon
                          value="action/mail_upload"
                          color="palettes.blue.30.100"
                          isNotFilled
                          size={20}
                        />

                        <Heading size={3}>
                          {i18n.t('entity.customer.labels.customer_message_title')}
                        </Heading>
                        <Text color="secondary" align="center">
                          {description}
                        </Text>
                      </VStack>
                    </Box>

                    <FormField
                      control={control}
                      name="customerMessageId"
                      type="chips"
                      options={messageTypeOptions}
                      label={i18n.t('general.labels.type')}
                      onChange={(value) => handleChipsChange(formApi, value)}
                    />

                    <CustomerSelectField
                      control={control}
                      formApi={formApi}
                      customerMessageId={messageId?.[0]}
                      defaultOptions={defaultCustomerOptions}
                    />

                    <VStack spacing={4}>
                      <MessageFormField
                        control={control}
                        formApi={formApi}
                        serviceCaseId={props.serviceCaseId}
                        serviceOrderId={props.serviceOrderId}
                      />

                      <MessageUpload
                        vehicleId={props.vehicleId}
                        resourceId={UploadFromSystemProps.resourceId}
                        recordId={UploadFromSystemProps.recordId}
                        uploadedFiles={uploadedFiles}
                        setUploadedFiles={setUploadedFiles}
                      />
                    </VStack>
                  </VStack>

                  <EmailPreview
                    formApi={formApi}
                    control={control}
                    serviceCaseId={props.serviceCaseId}
                    inspectionId={inspectionId}
                  />

                  <GridItem span={2}>
                    <ButtonGroup align="right">
                      <Button
                        onClick={closeCurrentDialog}
                        variant="secondary"
                        title={i18n.t('general.actions.cancel')}
                      />

                      <FormButton
                        control={control}
                        type="submit"
                        title={i18n.t('general.actions.send')}
                      />
                    </ButtonGroup>
                  </GridItem>
                </Grid>
              );
            }}
          </Form>
        </motion.div>
      </Box>
    </DataStatus>
  );
}
